import React, { useState, useRef, useMemo } from 'react';
import { BarChart3, Download, ChevronRight, ChevronLeft, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Matriz de Maturidade BIM baseada em Bilal Succar
const maturityMatrix = {
  technology: {
    name: 'Tecnologia',
    description: 'Ferramentas, softwares e infraestrutura de hardware',
    questions: [
      {
        id: 'tech1',
        question: 'Qual o nível de adoção de softwares BIM na organização?',
        options: [
          { value: 0, label: 'Não utilizamos softwares BIM', level: 'Inicial' },
          { value: 1, label: 'Uso isolado por alguns profissionais', level: 'Definido' },
          { value: 2, label: 'Uso padronizado em projetos piloto', level: 'Gerenciado' },
          { value: 3, label: 'Uso integrado entre disciplinas', level: 'Integrado' },
          { value: 4, label: 'Uso otimizado com automação e IA', level: 'Otimizado' }
        ]
      },
      {
        id: 'tech2',
        question: 'Como é a infraestrutura de hardware para trabalho com BIM?',
        options: [
          { value: 0, label: 'Equipamentos inadequados para BIM', level: 'Inicial' },
          { value: 1, label: 'Alguns equipamentos compatíveis', level: 'Definido' },
          { value: 2, label: 'Hardware adequado para a maioria', level: 'Gerenciado' },
          { value: 3, label: 'Hardware otimizado para BIM', level: 'Integrado' },
          { value: 4, label: 'Infraestrutura cloud e colaborativa', level: 'Otimizado' }
        ]
      },
      {
        id: 'tech3',
        question: 'Existe um Ambiente Comum de Dados (CDE)?',
        options: [
          { value: 0, label: 'Não, arquivos em pastas locais', level: 'Inicial' },
          { value: 1, label: 'Pastas compartilhadas na rede', level: 'Definido' },
          { value: 2, label: 'CDE básico implementado', level: 'Gerenciado' },
          { value: 3, label: 'CDE com workflows definidos', level: 'Integrado' },
          { value: 4, label: 'CDE integrado com automações', level: 'Otimizado' }
        ]
      },
      {
        id: 'tech4',
        question: 'Qual o nível de interoperabilidade entre softwares?',
        options: [
          { value: 0, label: 'Nenhuma troca de dados estruturada', level: 'Inicial' },
          { value: 1, label: 'Troca via formatos proprietários', level: 'Definido' },
          { value: 2, label: 'Uso básico de IFC', level: 'Gerenciado' },
          { value: 3, label: 'IFC otimizado com MVDs', level: 'Integrado' },
          { value: 4, label: 'OpenBIM completo e APIs integradas', level: 'Otimizado' }
        ]
      }
    ]
  },
  process: {
    name: 'Processos',
    description: 'Fluxos de trabalho, metodologias e procedimentos',
    questions: [
      {
        id: 'proc1',
        question: 'Existem processos documentados para trabalho BIM?',
        options: [
          { value: 0, label: 'Não há processos definidos', level: 'Inicial' },
          { value: 1, label: 'Processos informais e variáveis', level: 'Definido' },
          { value: 2, label: 'Processos documentados básicos', level: 'Gerenciado' },
          { value: 3, label: 'Processos integrados e revisados', level: 'Integrado' },
          { value: 4, label: 'Melhoria contínua de processos', level: 'Otimizado' }
        ]
      },
      {
        id: 'proc2',
        question: 'Como é a colaboração entre disciplinas de projeto?',
        options: [
          { value: 0, label: 'Trabalho isolado por disciplina', level: 'Inicial' },
          { value: 1, label: 'Reuniões ocasionais de coordenação', level: 'Definido' },
          { value: 2, label: 'Coordenação BIM periódica', level: 'Gerenciado' },
          { value: 3, label: 'Colaboração em tempo real', level: 'Integrado' },
          { value: 4, label: 'Colaboração preditiva com IA', level: 'Otimizado' }
        ]
      },
      {
        id: 'proc3',
        question: 'Qual o nível de detecção e resolução de conflitos?',
        options: [
          { value: 0, label: 'Conflitos detectados em obra', level: 'Inicial' },
          { value: 1, label: 'Verificação manual eventual', level: 'Definido' },
          { value: 2, label: 'Clash detection regular', level: 'Gerenciado' },
          { value: 3, label: 'Clash detection automatizado', level: 'Integrado' },
          { value: 4, label: 'Prevenção de conflitos com IA', level: 'Otimizado' }
        ]
      },
      {
        id: 'proc4',
        question: 'Como é o controle de qualidade dos modelos BIM?',
        options: [
          { value: 0, label: 'Não há controle de qualidade', level: 'Inicial' },
          { value: 1, label: 'Verificação visual eventual', level: 'Definido' },
          { value: 2, label: 'Checklists de verificação', level: 'Gerenciado' },
          { value: 3, label: 'Validação automatizada', level: 'Integrado' },
          { value: 4, label: 'IA para garantia de qualidade', level: 'Otimizado' }
        ]
      }
    ]
  },
  policy: {
    name: 'Políticas',
    description: 'Normas, padrões, contratos e diretrizes organizacionais',
    questions: [
      {
        id: 'pol1',
        question: 'Existem padrões BIM definidos na organização?',
        options: [
          { value: 0, label: 'Não há padrões definidos', level: 'Inicial' },
          { value: 1, label: 'Padrões informais variáveis', level: 'Definido' },
          { value: 2, label: 'Padrões documentados básicos', level: 'Gerenciado' },
          { value: 3, label: 'Padrões alinhados com normas', level: 'Integrado' },
          { value: 4, label: 'Padrões certificados ISO 19650', level: 'Otimizado' }
        ]
      },
      {
        id: 'pol2',
        question: 'Como o BIM é tratado nos contratos?',
        options: [
          { value: 0, label: 'BIM não consta em contratos', level: 'Inicial' },
          { value: 1, label: 'Menção genérica ao BIM', level: 'Definido' },
          { value: 2, label: 'Requisitos BIM básicos', level: 'Gerenciado' },
          { value: 3, label: 'EIR e BEP detalhados', level: 'Integrado' },
          { value: 4, label: 'Contratos BIM maduros com SLAs', level: 'Otimizado' }
        ]
      },
      {
        id: 'pol3',
        question: 'Qual o nível de definição de LOD/LOI?',
        options: [
          { value: 0, label: 'Não utilizamos LOD/LOI', level: 'Inicial' },
          { value: 1, label: 'Conceito conhecido mas não aplicado', level: 'Definido' },
          { value: 2, label: 'LOD básico por fase', level: 'Gerenciado' },
          { value: 3, label: 'LOD/LOI detalhado por elemento', level: 'Integrado' },
          { value: 4, label: 'LOD/LOI automatizado e validado', level: 'Otimizado' }
        ]
      },
      {
        id: 'pol4',
        question: 'Existe um BIM Mandate ou diretriz organizacional?',
        options: [
          { value: 0, label: 'Não existe diretriz BIM', level: 'Inicial' },
          { value: 1, label: 'Iniciativas isoladas', level: 'Definido' },
          { value: 2, label: 'BIM Mandate básico', level: 'Gerenciado' },
          { value: 3, label: 'BIM Mandate completo e ativo', level: 'Integrado' },
          { value: 4, label: 'Governança BIM madura', level: 'Otimizado' }
        ]
      }
    ]
  },
  people: {
    name: 'Pessoas',
    description: 'Competências, treinamento e cultura organizacional',
    questions: [
      {
        id: 'peo1',
        question: 'Qual o nível de competência BIM da equipe?',
        options: [
          { value: 0, label: 'Sem conhecimento de BIM', level: 'Inicial' },
          { value: 1, label: 'Conhecimento básico em poucos', level: 'Definido' },
          { value: 2, label: 'Equipe treinada em BIM', level: 'Gerenciado' },
          { value: 3, label: 'Especialistas BIM na equipe', level: 'Integrado' },
          { value: 4, label: 'Centro de excelência BIM', level: 'Otimizado' }
        ]
      },
      {
        id: 'peo2',
        question: 'Como é a estrutura de papéis BIM?',
        options: [
          { value: 0, label: 'Não há papéis BIM definidos', level: 'Inicial' },
          { value: 1, label: 'Papéis informais', level: 'Definido' },
          { value: 2, label: 'BIM Manager designado', level: 'Gerenciado' },
          { value: 3, label: 'Equipe BIM estruturada', level: 'Integrado' },
          { value: 4, label: 'Organização orientada a BIM', level: 'Otimizado' }
        ]
      },
      {
        id: 'peo3',
        question: 'Existe programa de capacitação BIM?',
        options: [
          { value: 0, label: 'Não há treinamentos BIM', level: 'Inicial' },
          { value: 1, label: 'Treinamentos eventuais', level: 'Definido' },
          { value: 2, label: 'Programa de treinamento básico', level: 'Gerenciado' },
          { value: 3, label: 'Trilhas de aprendizado BIM', level: 'Integrado' },
          { value: 4, label: 'Universidade corporativa BIM', level: 'Otimizado' }
        ]
      },
      {
        id: 'peo4',
        question: 'Como é a cultura de inovação e mudança?',
        options: [
          { value: 0, label: 'Resistência à mudança', level: 'Inicial' },
          { value: 1, label: 'Aceitação passiva', level: 'Definido' },
          { value: 2, label: 'Abertura a novas práticas', level: 'Gerenciado' },
          { value: 3, label: 'Cultura de melhoria contínua', level: 'Integrado' },
          { value: 4, label: 'Inovação como valor central', level: 'Otimizado' }
        ]
      }
    ]
  }
};

const BIMMaturityAssessment = () => {
  const [currentCategory, setCurrentCategory] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const reportRef = useRef(null);
  
  // Logo base64 for PDF report - AJ Solução BIM (embedded to avoid CORS issues)
  const companyLogoBase64 = 'data:image/png;base64,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';

  const categories = Object.keys(maturityMatrix);
  const currentCategoryKey = categories[currentCategory];
  const currentCategoryData = maturityMatrix[currentCategoryKey];

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const calculateResults = () => {
    const results = {};
    let totalScore = 0;
    let totalQuestions = 0;

    categories.forEach(cat => {
      const catData = maturityMatrix[cat];
      let catScore = 0;
      let catQuestions = 0;

      catData.questions.forEach(q => {
        if (answers[q.id] !== undefined) {
          catScore += answers[q.id];
          catQuestions++;
        }
      });

      const avgScore = catQuestions > 0 ? catScore / catQuestions : 0;
      results[cat] = {
        score: avgScore,
        percentage: (avgScore / 4) * 100,
        level: getMaturityLevel(avgScore),
        answered: catQuestions,
        total: catData.questions.length
      };

      totalScore += catScore;
      totalQuestions += catQuestions;
    });

    const overallAvg = totalQuestions > 0 ? totalScore / totalQuestions : 0;
    results.overall = {
      score: overallAvg,
      percentage: (overallAvg / 4) * 100,
      level: getMaturityLevel(overallAvg)
    };

    return results;
  };

  const getMaturityLevel = (score) => {
    if (score < 0.5) return { name: 'Inicial', color: 'red', description: 'Práticas BIM não estabelecidas' };
    if (score < 1.5) return { name: 'Definido', color: 'orange', description: 'Práticas BIM em desenvolvimento' };
    if (score < 2.5) return { name: 'Gerenciado', color: 'yellow', description: 'Práticas BIM padronizadas' };
    if (score < 3.5) return { name: 'Integrado', color: 'blue', description: 'Práticas BIM integradas' };
    return { name: 'Otimizado', color: 'green', description: 'Excelência em BIM' };
  };

  const getColorClass = (color) => {
    const colors = {
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      yellow: 'bg-yellow-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  const getTextColorClass = (color) => {
    const colors = {
      red: 'text-red-500',
      orange: 'text-orange-500',
      yellow: 'text-yellow-500',
      blue: 'text-blue-500',
      green: 'text-green-500'
    };
    return colors[color] || 'text-gray-500';
  };

  const isCurrentCategoryComplete = () => {
    return currentCategoryData.questions.every(q => answers[q.id] !== undefined);
  };

  const getTotalAnswered = () => {
    return Object.keys(answers).length;
  };

  const getTotalQuestions = () => {
    return categories.reduce((acc, cat) => acc + maturityMatrix[cat].questions.length, 0);
  };

  const canShowResults = () => {
    return getTotalAnswered() === getTotalQuestions();
  };

  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    setIsGeneratingPDF(true);
    
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: '#111111',
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save('Relatorio_Maturidade_BIM.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF do relatório de maturidade:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const results = showResults ? calculateResults() : null;

  const strengths = useMemo(() => {
    if (!results) return [];
    return categories
      .filter(cat => results[cat].percentage >= 50)
      .sort((a, b) => results[b].percentage - results[a].percentage)
      .slice(0, 3);
  }, [results, categories]);

  const weaknesses = useMemo(() => {
    if (!results) return [];
    return categories
      .filter(cat => results[cat].percentage < 50)
      .sort((a, b) => results[a].percentage - results[b].percentage)
      .slice(0, 3);
  }, [results, categories]);

  const strengthsCount = results ? categories.filter(cat => results[cat].percentage >= 50).length : 0;
  const weaknessesCount = results ? categories.filter(cat => results[cat].percentage < 50).length : 0;

  if (showResults && results) {
    return (
      <section className="py-20 bg-zinc-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div ref={reportRef} className="bg-zinc-900 p-8 rounded-2xl">
              {/* Header do Relatório com Logo */}
              <div className="text-center mb-10">
                <div className="flex justify-center mb-6">
                  <img 
                    src={companyLogoBase64} 
                    alt="AJ Solução BIM" 
                    className="h-16 object-contain"
                  />
                </div>
                <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">
                  Relatório de Maturidade BIM
                </Badge>
                <h2 className="text-3xl font-bold text-white mb-2">
                  Resultado da Avaliação
                </h2>
                <p className="text-gray-400">
                  Baseado na metodologia de Bilal Succar
                </p>
              </div>

              {/* Nível Geral */}
              <div className="bg-black rounded-xl p-8 mb-8 text-center">
                <p className="text-gray-400 mb-2">Nível de Maturidade BIM Geral</p>
                <div className={`text-5xl font-bold ${getTextColorClass(results.overall.level.color)} mb-2`}>
                  {results.overall.level.name}
                </div>
                <p className="text-gray-300">{results.overall.level.description}</p>
                <div className="mt-4">
                  <div className="w-full bg-zinc-800 rounded-full h-4">
                    <div 
                      className={`h-4 rounded-full ${getColorClass(results.overall.level.color)}`}
                      style={{ width: `${results.overall.percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-gray-400 mt-2">{results.overall.percentage.toFixed(1)}% de maturidade</p>
                </div>
              </div>

              {/* Gráfico por Área */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-6">Análise por Área</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {categories.map(cat => {
                    const catResult = results[cat];
                    const catData = maturityMatrix[cat];
                    return (
                      <Card key={cat} className="bg-black border-zinc-800">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-white">{catData.name}</CardTitle>
                            <Badge className={`${getColorClass(catResult.level.color)} text-white`}>
                              {catResult.level.name}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-400">Pontuação</span>
                              <span className="text-white">{catResult.percentage.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-zinc-800 rounded-full h-3">
                              <div 
                                className={`h-3 rounded-full ${getColorClass(catResult.level.color)}`}
                                style={{ width: `${catResult.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <p className="text-gray-500 text-sm">{catData.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Pontos Fortes e Fracos */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="bg-green-900/20 border-green-500/30">
                  <CardHeader>
                    <CardTitle className="text-green-400 flex items-center gap-2">
                      <CheckCircle size={20} />
                      Pontos Fortes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {strengths.map(cat => (
                          <li key={cat} className="text-gray-300 flex items-center gap-2">
                            <CheckCircle className="text-green-500" size={16} />
                            {maturityMatrix[cat].name} ({results[cat].percentage.toFixed(0)}%)
                          </li>
                        ))}
                      {strengthsCount === 0 && (
                        <li className="text-gray-500">Nenhum ponto forte identificado ainda</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="bg-red-900/20 border-red-500/30">
                  <CardHeader>
                    <CardTitle className="text-red-400 flex items-center gap-2">
                      <AlertTriangle size={20} />
                      Pontos a Melhorar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {weaknesses.map(cat => (
                          <li key={cat} className="text-gray-300 flex items-center gap-2">
                            <AlertTriangle className="text-red-500" size={16} />
                            {maturityMatrix[cat].name} ({results[cat].percentage.toFixed(0)}%)
                          </li>
                        ))}
                      {weaknessesCount === 0 && (
                        <li className="text-gray-500">Excelente! Nenhum ponto crítico</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Recomendações */}
              <Card className="bg-blue-900/20 border-blue-500/30 mb-8">
                <CardHeader>
                  <CardTitle className="text-blue-400 flex items-center gap-2">
                    <Info size={20} />
                    Recomendações
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-gray-300">
                    {results.overall.percentage < 25 && (
                      <p>• <strong>Fase Inicial:</strong> Recomendamos iniciar com um diagnóstico detalhado e plano de implementação BIM estruturado.</p>
                    )}
                    {results.overall.percentage >= 25 && results.overall.percentage < 50 && (
                      <p>• <strong>Fase de Desenvolvimento:</strong> Foque em padronizar processos e capacitar a equipe para avançar na maturidade.</p>
                    )}
                    {results.overall.percentage >= 50 && results.overall.percentage < 75 && (
                      <p>• <strong>Fase de Consolidação:</strong> Integre as práticas BIM entre disciplinas e implemente automações.</p>
                    )}
                    {results.overall.percentage >= 75 && (
                      <p>• <strong>Fase de Otimização:</strong> Continue investindo em inovação e considere certificações como ISO 19650.</p>
                    )}
                    <p>• Entre em contato com a equipe AJ BIM para um diagnóstico personalizado e plano de evolução.</p>
                  </div>
                </CardContent>
              </Card>

              {/* Footer do Relatório */}
              <div className="text-center pt-6 border-t border-zinc-800">
                <div className="flex justify-center mb-3">
                  <img 
                    src={companyLogoBase64} 
                    alt="AJ Solução BIM" 
                    className="h-10 object-contain opacity-80"
                  />
                </div>
                <p className="text-gray-500 text-sm">Relatório gerado em {new Date().toLocaleDateString('pt-BR')} | AJ BIM - Metodologia Justi</p>
                <p className="text-gray-500 text-sm">Baseado na Matriz de Maturidade BIM de Bilal Succar</p>
                <p className="text-gray-600 text-xs mt-2">www.bimportal.com.br | contato@bimportal.com.br</p>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-center gap-4 mt-8">
              <Button
                onClick={generatePDF}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={isGeneratingPDF}
              >
                <Download className="mr-2" size={20} />
                {isGeneratingPDF ? 'Gerando PDF...' : 'Baixar Relatório PDF'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowResults(false);
                  setAnswers({});
                  setCurrentCategory(0);
                }}
                className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
              >
                Refazer Avaliação
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-zinc-900">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">
              Avaliação de Maturidade BIM
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4">
              Matriz de Maturidade BIM - Bilal Succar
            </h2>
            <p className="text-gray-400">
              Responda as perguntas para descobrir o nível de maturidade BIM da sua organização
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm">
                Categoria {currentCategory + 1} de {categories.length}
              </span>
              <span className="text-gray-400 text-sm">
                {getTotalAnswered()} de {getTotalQuestions()} perguntas respondidas
              </span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-red-600 transition-all duration-300"
                style={{ width: `${(getTotalAnswered() / getTotalQuestions()) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat, index) => {
              const catComplete = maturityMatrix[cat].questions.every(q => answers[q.id] !== undefined);
              return (
                <button
                  key={cat}
                  onClick={() => setCurrentCategory(index)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                    ${index === currentCategory 
                      ? 'bg-red-600 text-white' 
                      : catComplete 
                        ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                        : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                    }`}
                >
                  {catComplete && <CheckCircle className="inline mr-1" size={14} />}
                  {maturityMatrix[cat].name}
                </button>
              );
            })}
          </div>

          {/* Questions */}
          <Card className="bg-black border-zinc-800 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold">{currentCategory + 1}</span>
                </div>
                {currentCategoryData.name}
              </CardTitle>
              <p className="text-gray-400 text-sm">{currentCategoryData.description}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {currentCategoryData.questions.map((question, qIndex) => (
                <div key={question.id} className="border-b border-zinc-800 pb-6 last:border-0">
                  <p className="text-white font-medium mb-4">
                    {qIndex + 1}. {question.question}
                  </p>
                  <div className="space-y-2">
                    {question.options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(question.id, option.value)}
                        className={`w-full text-left p-3 rounded-lg border transition-all
                          ${answers[question.id] === option.value
                            ? 'border-red-500 bg-red-600/20 text-white'
                            : 'border-zinc-700 bg-zinc-900 text-gray-300 hover:border-zinc-600'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{option.label}</span>
                          <Badge variant="outline" className="border-zinc-600 text-gray-500 text-xs">
                            {option.level}
                          </Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setCurrentCategory(prev => Math.max(0, prev - 1))}
              disabled={currentCategory === 0}
              className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
            >
              <ChevronLeft className="mr-2" size={20} />
              Anterior
            </Button>

            {currentCategory < categories.length - 1 ? (
              <Button
                onClick={() => setCurrentCategory(prev => prev + 1)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Próxima
                <ChevronRight className="ml-2" size={20} />
              </Button>
            ) : (
              <Button
                onClick={() => setShowResults(true)}
                disabled={!canShowResults()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <BarChart3 className="mr-2" size={20} />
                Ver Resultado
              </Button>
            )}
          </div>

          {!canShowResults() && currentCategory === categories.length - 1 && (
            <p className="text-center text-yellow-500 mt-4 text-sm">
              <AlertTriangle className="inline mr-2" size={16} />
              Responda todas as perguntas para ver o resultado
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default BIMMaturityAssessment;
